import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ProfileFormData } from "../../types";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { LANGUAGES } from "../../constants/languages";

interface LanguagesFieldProps {
  form: UseFormReturn<ProfileFormData>;
}

export const LanguagesField = ({ form }: LanguagesFieldProps) => {
  const selectedLanguages = form.watch('languages').split(',').map(lang => lang.trim()).filter(Boolean);

  const handleSelectLanguage = (language: string) => {
    const current = form.getValues('languages').split(',').map(lang => lang.trim()).filter(Boolean);
    if (!current.includes(language)) {
      const newLanguages = [...current, language].join(', ');
      form.setValue('languages', newLanguages);
    }
  };

  const handleRemoveLanguage = (languageToRemove: string) => {
    const newLanguages = form.getValues('languages')
      .split(',')
      .map(lang => lang.trim())
      .filter(lang => lang !== languageToRemove)
      .join(', ');
    form.setValue('languages', newLanguages);
  };

  return (
    <FormField
      control={form.control}
      name="languages"
      render={({ field }) => (
        <FormItem className="flex flex-col gap-2">
          <FormLabel>Languages</FormLabel>
          <FormControl>
            <Command className="rounded-lg border shadow-sm">
              <CommandInput placeholder="Search languages..." />
              <CommandList>
                <CommandEmpty>No language found.</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  {LANGUAGES.map((language) => (
                    <CommandItem
                      key={language}
                      onSelect={() => handleSelectLanguage(language)}
                      className="cursor-pointer"
                    >
                      {language}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </FormControl>
          <div className="flex flex-wrap gap-2">
            {selectedLanguages.map((language) => (
              <Badge
                key={language}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {language}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => handleRemoveLanguage(language)}
                />
              </Badge>
            ))}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};